<template>
  <div class="staff-management">
    <div class="page-header">
      <div>
        <h2 class="page-title">员工管理</h2>
        <p class="page-desc">管理门店员工和核销员账号</p>
      </div>
      <el-button type="primary" size="large" @click="openAddDialog">
        <el-icon>
          <Plus />
        </el-icon>
        添加员工
      </el-button>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-value">{{ stats.total || 0 }}</div>
        <div class="stat-label">总员工</div>
      </div>
      <div class="stat-card success">
        <div class="stat-value">{{ stats.active || 0 }}</div>
        <div class="stat-label">在职</div>
      </div>
      <div class="stat-card warning">
        <div class="stat-value">{{ stats.admins || 0 }}</div>
        <div class="stat-label">管理员</div>
      </div>
      <div class="stat-card primary">
        <div class="stat-value">{{ stats.clerks || 0 }}</div>
        <div class="stat-label">核销员</div>
      </div>
    </div>

    <!-- 搜索筛选栏 -->
    <div class="filter-bar">
      <el-input v-model="searchKey" placeholder="搜索姓名/手机号/工号" prefix-icon="Search" clearable style="width: 260px"
        @keyup="($event) => $event.key === 'Enter' && handleSearch()" />
      <el-select v-model="roleFilter" placeholder="全部角色" clearable style="width: 120px" @change="handleSearch">
        <el-option label="管理员" value="admin" />
        <el-option label="核销员" value="clerk" />
      </el-select>
      <el-select v-model="storeFilter" placeholder="全部门店" clearable style="width: 180px" @change="onStoreChange">
        <el-option v-for="store in storeList" :key="store.id" :label="store.store_name" :value="store.id" />
      </el-select>
      <el-select v-model="branchFilter" placeholder="全部分店" clearable style="width: 180px" @change="handleSearch">
        <el-option v-for="branch in branchList" :key="branch.id" :label="branch.branch_name" :value="branch.id" />
      </el-select>
      <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px" @change="handleSearch">
        <el-option label="在职" :value="1" />
        <el-option label="离职" :value="0" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

<!-- 员工表格 - 修改列宽 -->
<el-table :data="staffList" border v-loading="loading" style="width: 100%" class="staff-table">
  <el-table-column label="员工信息" min-width="200">
    <template #default="{ row }">
      <div class="staff-info">
        <el-avatar :size="40" :src="row.avatar || defaultAvatar" />
        <div class="staff-detail">
          <div class="staff-name">{{ row.name }}</div>
          <div class="staff-no">工号：{{ row.staff_no }}</div>
        </div>
      </div>
    </template>
  </el-table-column>

  <el-table-column prop="phone" label="手机号" min-width="120" />

  <el-table-column label="所属门店" min-width="150">
    <template #default="{ row }">
      {{ getStoreName(row.branch_id) }}
    </template>
  </el-table-column>

  <el-table-column label="所属分店" min-width="150">
    <template #default="{ row }">
      {{ getBranchName(row.store_branch_id) }}
    </template>
  </el-table-column>

  <el-table-column label="角色" width="90" align="center">
    <template #default="{ row }">
      <el-tag :type="row.role === 'admin' ? 'danger' : 'success'" size="small">
        {{ row.role === 'admin' ? '管理员' : '核销员' }}
      </el-tag>
    </template>
  </el-table-column>

  <el-table-column label="状态" width="80" align="center">
    <template #default="{ row }">
      <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
        {{ row.status === 1 ? '在职' : '离职' }}
      </el-tag>
    </template>
  </el-table-column>

  <el-table-column label="最后登录" width="160">
    <template #default="{ row }">
      {{ formatTime(row.last_login_time) || '--' }}
    </template>
  </el-table-column>

  <el-table-column label="创建时间" width="160">
    <template #default="{ row }">
      {{ formatTime(row.create_time) }}
    </template>
  </el-table-column>

  <el-table-column label="操作" width="200" fixed="right" align="center">
    <template #default="{ row }">
      <el-button type="primary" link size="small" @click="openEditDialog(row)">
        <el-icon><Edit /></el-icon> 编辑
      </el-button>
      <el-button :type="row.status === 1 ? 'warning' : 'success'" link size="small" @click="toggleStatus(row)">
        <el-icon><Switch /></el-icon> {{ row.status === 1 ? '离职' : '复职' }}
      </el-button>
      <el-popconfirm title="确定删除该员工吗？" @confirm="handleDelete(row.id)">
        <template #reference>
          <el-button type="danger" link size="small">
            <el-icon><Delete /></el-icon> 删除
          </el-button>
        </template>
      </el-popconfirm>
    </template>
  </el-table-column>
</el-table>
    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="pagination.total > pagination.limit">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData" @current-change="fetchData" />
    </div>

    <!-- 添加/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑员工' : '添加员工'" width="550px" destroy-on-close>
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
        <el-form-item label="姓名" prop="name" required>
          <el-input v-model="formData.name" placeholder="请输入姓名" />
        </el-form-item>

        <el-form-item label="手机号" prop="phone" required>
          <el-input v-model="formData.phone" placeholder="请输入手机号" />
        </el-form-item>

        <el-form-item label="工号">
          <el-input v-model="formData.staff_no" placeholder="自动生成" disabled />
        </el-form-item>

        <el-form-item label="所属门店" prop="branch_id">
          <el-select v-model="formData.branch_id" placeholder="请选择所属门店" clearable style="width: 100%"
            @change="onFormStoreChange">
            <el-option v-for="store in storeList" :key="store.id" :label="store.store_name" :value="store.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="所属分店">
          <el-select v-model="formData.store_branch_id" placeholder="请先选择门店" clearable style="width: 100%">
            <el-option v-for="branch in formBranchList" :key="branch.id" :label="branch.branch_name"
              :value="branch.id" />
          </el-select>
          <div class="form-tip">可选，不选表示在总店工作</div>
        </el-form-item>

        <el-form-item label="角色" prop="role" required>
          <el-radio-group v-model="formData.role">
            <el-radio label="admin">管理员</el-radio>
            <el-radio label="clerk">核销员</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="登录密码" prop="password" :required="!isEditMode">
          <el-input v-model="formData.password" type="password" placeholder="请输入密码" show-password />
          <div v-if="isEditMode" class="form-tip">留空则不修改密码</div>
        </el-form-item>

        <el-form-item label="状态">
          <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="在职"
            inactive-text="离职" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { Plus, Edit, Delete, Switch } from '@element-plus/icons-vue'

export default {
  name: 'StaffManagementPage',
  components: { Plus, Edit, Delete, Switch },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)
    const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

    const loading = ref(false)
    const submitting = ref(false)
    const searchKey = ref('')
    const roleFilter = ref(null)
    const storeFilter = ref(null)
    const branchFilter = ref(null)
    const statusFilter = ref(null)
    const staffList = ref([])
    const storeList = ref([])
    const branchList = ref([])
    const formBranchList = ref([])
    const stats = ref({})

    const dialogVisible = ref(false)
    const isEditMode = ref(false)
    const formRef = ref(null)

    const formData = reactive({
      id: null,
      name: '',
      phone: '',
      staff_no: '',
      branch_id: null,
      store_branch_id: null,
      role: 'clerk',
      password: '',
      status: 1
    })

    const formRules = {
      name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
      phone: [
        { required: true, message: '请输入手机号', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
      ],
      password: [
        {
          required: true, message: '请输入密码', trigger: 'blur', validator: (rule, value, callback) => {
            if (!isEditMode.value && !value) {
              callback(new Error('请输入密码'))
            } else {
              callback()
            }
          }
        }
      ]
    }

    const pagination = reactive({
      page: 1,
      limit: 10,
      total: 0
    })

    const formatTime = (time) => {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    }

    const getStoreName = (branchId) => {
      if (!branchId) return '未分配'
      const store = storeList.value.find(s => s.id === branchId)
      return store ? store.store_name : '未知门店'
    }

    const getBranchName = (branchId) => {
      if (!branchId) return '未分配'
      const branch = branchList.value.find(b => b.id === branchId)
      return branch ? branch.branch_name : '未知分店'
    }

    const generateStaffNo = () => {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
      return `STF${year}${month}${day}${random}`
    }

    const fetchStores = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          storeList.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取门店失败:', error)
      }
    }

    const fetchBranches = async (storeIdParam = null) => {
      try {
        const params = {
          agent_id: agentId.value,
          store_id: storeId.value,
          limit: 100
        }
        if (storeIdParam) {
          params.branch_id = storeIdParam
        }
        const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, { params })
        if (response.data.code === 200) {
          branchList.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取分店失败:', error)
      }
    }

    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          agent_id: agentId.value,
          store_id: storeId.value,
          page: pagination.page,
          limit: pagination.limit,
          search: searchKey.value,
          role: roleFilter.value,
          status: statusFilter.value
        }
        if (storeFilter.value) {
          params.branch_id = storeFilter.value
        }
        if (branchFilter.value) {
          params.store_branch_id = branchFilter.value
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/staff_api.php`, { params })

        if (response.data.code === 200) {
          staffList.value = response.data.data.list
          stats.value = response.data.data.stats
          pagination.total = response.data.data.pagination.total
        }
      } catch (error) {
        console.error('获取员工列表失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        loading.value = false
      }
    }

    const onStoreChange = async () => {
      branchFilter.value = null
      if (storeFilter.value) {
        await fetchBranches(storeFilter.value)
      } else {
        await fetchBranches()
      }
      fetchData()
    }

    const onFormStoreChange = async () => {
      formData.store_branch_id = null
      if (formData.branch_id) {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, branch_id: formData.branch_id, limit: 100 }
          })
          if (response.data.code === 200) {
            formBranchList.value = response.data.data.list
          }
        } catch (error) {
          console.error('获取分店失败:', error)
          formBranchList.value = []
        }
      } else {
        formBranchList.value = []
      }
    }

    const handleSearch = () => {
      pagination.page = 1
      fetchData()
    }

    const resetSearch = () => {
      searchKey.value = ''
      roleFilter.value = null
      storeFilter.value = null
      branchFilter.value = null
      statusFilter.value = null
      pagination.page = 1
      fetchData()
    }

    const openAddDialog = () => {
      isEditMode.value = false
      formBranchList.value = []
      Object.assign(formData, {
        id: null,
        name: '',
        phone: '',
        staff_no: generateStaffNo(),
        branch_id: null,
        store_branch_id: null,
        role: 'clerk',
        password: '',
        status: 1
      })
      dialogVisible.value = true
    }

    const openEditDialog = async (staff) => {
      isEditMode.value = true
      Object.assign(formData, {
        id: staff.id,
        name: staff.name,
        phone: staff.phone,
        staff_no: staff.staff_no,
        branch_id: staff.branch_id,
        store_branch_id: staff.store_branch_id,
        role: staff.role,
        password: '',
        status: staff.status
      })

      // 如果有所属门店，加载对应的分店列表
      if (staff.branch_id) {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, branch_id: staff.branch_id, limit: 100 }
          })
          if (response.data.code === 200) {
            formBranchList.value = response.data.data.list
          }
        } catch (error) {
          console.error('获取分店失败:', error)
          formBranchList.value = []
        }
      } else {
        formBranchList.value = []
      }

      dialogVisible.value = true
    }
    const submitForm = async () => {
      if (!formRef.value) return

      await formRef.value.validate(async (valid) => {
        if (!valid) return

        submitting.value = true
        const loadingInstance = ElLoading.service({ text: '保存中...' })

        try {
          const payload = {
            agent_id: agentId.value,
            store_id: storeId.value,
            ...formData
          }

          let response
          if (isEditMode.value) {
            if (!payload.password) {
              delete payload.password
            }
            response = await axios.put(`${config.BASE_URL}/shop/api/staff_api.php`, payload)
          } else {
            response = await axios.post(`${config.BASE_URL}/shop/api/staff_api.php`, payload)
          }

          if (response.data.code === 200) {
            ElMessage.success(isEditMode.value ? '更新成功' : '添加成功')
            dialogVisible.value = false
            fetchData()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('操作失败')
        } finally {
          submitting.value = false
          loadingInstance.close()
        }
      })
    }

    const toggleStatus = async (row) => {
      const newStatus = row.status === 1 ? 0 : 1
      const action = newStatus === 1 ? '复职' : '离职'

      const loadingInstance = ElLoading.service({ text: `${action}中...` })
      try {
        const response = await axios.put(`${config.BASE_URL}/shop/api/staff_api.php`, {
          id: row.id,
          store_id: storeId.value,
          status: newStatus
        })

        if (response.data.code === 200) {
          ElMessage.success(`${action}成功`)
          fetchData()
        } else {
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        console.error('操作失败:', error)
        ElMessage.error('操作失败')
      } finally {
        loadingInstance.close()
      }
    }

    const handleDelete = async (id) => {
      const loadingInstance = ElLoading.service({ text: '删除中...' })
      try {
        const response = await axios.delete(`${config.BASE_URL}/shop/api/staff_api.php`, {
          data: { id, store_id: storeId.value }
        })
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          fetchData()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        ElMessage.error('删除失败')
      } finally {
        loadingInstance.close()
      }
    }

    onMounted(() => {
      fetchStores()
      fetchBranches()
      fetchData()
    })

    return {
      loading,
      submitting,
      searchKey,
      roleFilter,
      storeFilter,
      branchFilter,
      statusFilter,
      staffList,
      storeList,
      branchList,
      formBranchList,
      stats,
      pagination,
      dialogVisible,
      isEditMode,
      formRef,
      formData,
      formRules,
      defaultAvatar,
      formatTime,
      getStoreName,
      getBranchName,
      onStoreChange,
      onFormStoreChange,
      handleSearch,
      resetSearch,
      openAddDialog,
      openEditDialog,
      submitForm,
      toggleStatus,
      handleDelete,
      fetchData
    }
  }
}
</script>

<style scoped lang="scss">
.staff-management {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .staff-table {
    width: 100%;
    overflow-x: auto;
    
    :deep(.el-table__body-wrapper) {
      overflow-x: auto;
    }
  }
  .stat-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;

    .stat-card {
      flex: 1;
      min-width: 100px;
      background: #fff;
      border-radius: 12px;
      padding: 16px 20px;
      text-align: center;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

      &.success .stat-value {
        color: #67c23a;
      }

      &.warning .stat-value {
        color: #e6a23c;
      }

      &.primary .stat-value {
        color: #409eff;
      }

      .stat-value {
        font-size: 28px;
        font-weight: 700;
      }

      .stat-label {
        font-size: 13px;
        color: #8c8c8c;
        margin-top: 4px;
      }
    }
  }

  .filter-bar {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }

  .staff-info {
    display: flex;
    gap: 12px;
    align-items: center;

    .staff-detail {
      .staff-name {
        font-weight: 500;
        margin-bottom: 2px;
      }

      .staff-no {
        font-size: 12px;
        color: #8c8c8c;
      }
    }
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }

  .form-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 4px;
  }
}
</style>